import { useEffect, useState, useRef } from 'react';
import { getPatientBlobUrl, OFFLINE_FILE_KEYS } from '../../services/offlineService';
import { trackBlobUrl } from '../../shared/utils/offlineUtils';

/**
 * @param {string} patientId
 * @param {string | null} offlinePatientId
 */
export function useOfflineAssetUrls(patientId, offlinePatientId) {
  const [offlineImageUrl, setOfflineImageUrl] = useState(/** @type {string | null} */ (null));
  const [offlineSegUrl, setOfflineSegUrl] = useState(/** @type {string | null} */ (null));
  const [offlineModelUrl, setOfflineModelUrl] = useState(/** @type {[string | null, string | null]} */ (null));
  const createdBlobUrlsRef = useRef(/** @type {string[]} */ ([]));

  useEffect(() => {
    const revokeCreatedBlobs = () => {
      createdBlobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      createdBlobUrlsRef.current = [];
    };

    if (!offlinePatientId) {
      revokeCreatedBlobs();
      setOfflineImageUrl(null);
      setOfflineSegUrl(null);
      setOfflineModelUrl(null);
      return;
    }

    if (!patientId) {
      return;
    }

    let cancelled = false;

    const loadOfflineUrls = async () => {
      const [imageUrl, segUrl, modelUrl] = await Promise.all([
        getPatientBlobUrl(patientId, OFFLINE_FILE_KEYS.MRI_VOLUME),
        getPatientBlobUrl(patientId, OFFLINE_FILE_KEYS.SEGMENTATION),
        getPatientBlobUrl(patientId, OFFLINE_FILE_KEYS.MODEL),
      ]);

      if (cancelled) return;

      setOfflineImageUrl(imageUrl ? trackBlobUrl(imageUrl, createdBlobUrlsRef) : null);
      setOfflineSegUrl(segUrl ? trackBlobUrl(segUrl, createdBlobUrlsRef) : null);
      setOfflineModelUrl(modelUrl ? trackBlobUrl(modelUrl, createdBlobUrlsRef) : null);
    };

    loadOfflineUrls();
    return () => {
      cancelled = true;
      revokeCreatedBlobs();
    };
  }, [patientId, offlinePatientId]);

  return { offlineImageUrl, offlineSegUrl, offlineModelUrl };
}
